/**
 * ThemeContext
 *
 * Light/dark mode state, persisted to localStorage under `theme` and
 * applied by toggling the `.dark` class on <html> (Tailwind's `darkMode:
 * 'class'` strategy). index.html applies the stored value inline before
 * React mounts to avoid a flash of the wrong theme on load.
 */
import { useCallback, useEffect, useState } from 'react';
import { ThemeContext } from './themeContextInstance.js';

/** @typedef {'light'|'dark'} Theme */

const THEME_STORAGE_KEY = 'theme';

/** @returns {Theme} */
function getInitialTheme() {
  const stored = /** @type {Theme|null} */ (localStorage.getItem(THEME_STORAGE_KEY));
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** @param {{ children: import('react').ReactNode }} props */
export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(/** @type {Theme} */ (getInitialTheme()));

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((next) => setThemeState(next), []);
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
